import { unwrapEnvelope } from "@/lib/api";
import { normalizeBuySources } from "@/components/buy-sources/normalizeSource";

const NON_COMPARABLE_STATUSES = new Set(["REVIEW", "CONFLICT"]);

export function verificationLabel(raw) {
  return String(raw?.verification_badge || raw?.verification_status || "").trim();
}

export function isComparableVerifiedSource(raw) {
  const label = verificationLabel(raw).toUpperCase();
  if (!label) return false;
  if (NON_COMPARABLE_STATUSES.has(label)) return false;
  return label === "VERIFIED" || label === "ESTIMATED";
}

/**
 * Parse POST /buy-sources/search response without re-ranking economics.
 * Uses backend arrays as authoritative: cheaper_sources, other_sources, unresolved_sources.
 */
export function parseBuySourcesSearchResponse(envelopeOrData) {
  const data = unwrapEnvelope(envelopeOrData) || {};

  const cheaperRaw = Array.isArray(data.cheaper_sources) ? data.cheaper_sources : [];
  const otherRaw = Array.isArray(data.other_sources) ? data.other_sources : [];
  const unresolvedRaw = Array.isArray(data.unresolved_sources) ? data.unresolved_sources : [];

  const walmartTargetPrice =
    data.walmart_target_price ??
    data.walmart?.target_price ??
    data.walmart?.buy_box_price ??
    data.target_walmart_price ??
    null;

  const cheaper_sources = normalizeBuySources(cheaperRaw).map((s) =>
    attachWalmartContext(s, walmartTargetPrice)
  );

  const other_sources = normalizeBuySources(
    otherRaw.filter(isComparableVerifiedSource)
  ).map((s) => attachWalmartContext(s, walmartTargetPrice));

  const unresolved_sources = normalizeBuySources(unresolvedRaw);

  return {
    cheaper_sources,
    other_sources,
    unresolved_sources,
    walmart_target_price: walmartTargetPrice,
    counts: data.counts || data.summary || {},
    request_id: data.request_id || null,
    walmart_item_id: data.walmart_item_id || null,
    elapsed_ms: data.elapsed_ms ?? data.search_time_ms ?? null,
    provider_calls: data.provider_calls ?? data.counts?.provider_calls ?? null,
    provider_status_class: data.provider_status_class || null,
    provider_status: data.provider_status || null,
    result_count: data.result_count ?? null,
  };
}

function attachWalmartContext(source, walmartTargetPrice) {
  if (walmartTargetPrice == null) return source;
  const equivalent = source.target_equivalent_cost ?? source.display_cost;
  if (equivalent == null || source.difference_vs_walmart != null) return source;
  return {
    ...source,
    walmart_target_price: walmartTargetPrice,
    difference_vs_walmart: roundMoney(equivalent - walmartTargetPrice),
  };
}

function roundMoney(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
}

export function buySourcesPanelState(result) {
  if (String(result?.provider_status || "").toLowerCase() === "unavailable") return "unavailable";
  const cheaper = result?.cheaper_sources || [];
  const other = result?.other_sources || [];
  const unresolved = result?.unresolved_sources || [];
  if (cheaper.length > 0) return "cheaper";
  if (other.length > 0) return "comparable_only";
  if (unresolved.length > 0) return "unresolved_only";
  return "empty";
}
